import { useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { SectionTitle } from 'shared/components/section'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import { Label } from 'shared/components/ui/label'
import { Textarea } from 'shared/components/ui/textarea'
import { useTranslate } from 'shared/lib/i18n'
import { formatDayTime } from 'shared/lib/utils/date'

/**
 * Closing a Point on «Локации» (ticket 45; ADR-0028, ADR-0033; user stories 61,
 * 133, 134). Every act carries a comment.
 *
 *  - **Закрыть временно / Открыть** — the partner learns nothing; the hub keeps the
 *    Point's Parcels, its Bags are not loaded, the storage on its shelf stands still;
 *    the Point itself works. The answer names the Bags already with drivers.
 *  - **Закрыть насовсем** — two steps: choose the recipient, read the preview (what
 *    moves, by where it is now; what goes to the partner as it is; what is in the
 *    way — each obstacle in words), then confirm. **Открыть снова** brings nothing
 *    back.
 *
 * The state, the counts and the refusals are the server's; the calls are injected
 * so the screen is tested without a backend.
 */
type Location = RouterOutputs['locations']['List'][number]
export type ClosurePreview = RouterOutputs['locations']['ClosurePreview']
export type CloseAnswer = RouterOutputs['locations']['Close']
export type OpenAnswer = RouterOutputs['locations']['Open']
export type CloseForGoodAnswer = RouterOutputs['locations']['CloseForGood']

export interface PointClosureCalls {
	close: (input: RouterInputs['locations']['Close']) => Promise<CloseAnswer>
	open: (input: RouterInputs['locations']['Open']) => Promise<OpenAnswer>
	preview: (input: RouterInputs['locations']['ClosurePreview']) => Promise<ClosurePreview>
	closeForGood: (input: RouterInputs['locations']['CloseForGood']) => Promise<CloseForGoodAnswer>
}

const viaTrpc: PointClosureCalls = {
	close: (input) => trpcClient.locations.Close.mutate(input),
	open: (input) => trpcClient.locations.Open.mutate(input),
	preview: (input) => trpcClient.locations.ClosurePreview.query(input),
	closeForGood: (input) => trpcClient.locations.CloseForGood.mutate(input),
}

const COUNTS = [
	'expected',
	'atHub',
	'inBagsAtHub',
	'onHubShelf',
	'withDrivers',
	'atOtherPoints',
	'onShelf',
	'expectedReturns',
] as const

/** Which act the admin is confirming: closing now, opening, opening again, or closing for good. */
type Mode = 'close' | 'open' | 'reopen' | 'forGood' | null

const SELECT_CLASS =
	'h-9 min-w-48 rounded-lg border border-input bg-transparent px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

export function PointClosure({
	location,
	points,
	onChanged,
	calls = viaTrpc,
}: {
	location: Location
	/** Every place, for the choice of a recipient. */
	points: readonly Location[]
	onChanged?: () => void
	calls?: PointClosureCalls
}) {
	const { t } = useTranslate()
	const [comment, setComment] = useState('')
	const [recipientId, setRecipientId] = useState('')
	const [preview, setPreview] = useState<ClosurePreview | null>(null)
	const [busy, setBusy] = useState(false)
	const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
	const [mode, setMode] = useState<Mode>(null)

	const recipients = points.filter(
		(p) => p.type === 'point' && p.migrated && !p.closedForGood && p._id !== location._id,
	)
	const refusalWords = (reason: string) => t(`closureRefusal.${reason}` as never)

	const run = async (act: () => Promise<{ ok: boolean; text: string } | null>) => {
		if (busy) return
		setBusy(true)
		setMessage(null)
		try {
			const result = await act()
			if (result) setMessage(result)
		} catch (error) {
			setMessage({ ok: false, text: t('pointClosure.failed', { message: failure(error) }) })
		} finally {
			setBusy(false)
		}
	}

	const needComment = () => {
		if (comment.trim()) return false
		setMessage({ ok: false, text: t('pointClosure.commentRequired') })
		return true
	}

	const onClose = () =>
		!needComment() &&
		run(async () => {
			const answer = await calls.close({ _id: location._id, comment })
			if (answer.outcome === 'refused') return { ok: false, text: refusalWords(answer.reason) }
			setComment('')
			setMode(null)
			onChanged?.()
			return { ok: true, text: t('pointClosure.closed', { count: String(answer.bagsWithDrivers) }) }
		})

	const onOpen = (reopen: boolean) =>
		!needComment() &&
		run(async () => {
			const answer = await calls.open({ _id: location._id, comment })
			if (answer.outcome === 'refused') return { ok: false, text: refusalWords(answer.reason) }
			setComment('')
			setMode(null)
			onChanged?.()
			return { ok: true, text: reopen ? t('pointClosure.reopened') : t('pointClosure.opened') }
		})

	const onPreview = () =>
		run(async () => {
			setPreview(await calls.preview({ _id: location._id, ...(recipientId ? { recipientId } : {}) }))
			return null
		})

	const onCloseForGood = () =>
		!needComment() &&
		run(async () => {
			const answer = await calls.closeForGood({ _id: location._id, recipientId, comment })
			if (answer.outcome === 'refused') {
				if (answer.preview) setPreview(answer.preview)
				return { ok: false, text: refusalWords(answer.reason) }
			}
			setComment('')
			setPreview(null)
			setMode(null)
			onChanged?.()
			return {
				ok: true,
				text: t(answer.outcome === 'closed' ? 'pointClosure.closedForGood' : 'pointClosure.alreadyClosed', {
					recipient: answer.recipient.id,
					count: String(answer.readdressed),
				}),
			}
		})

	const closedForGood = location.closedForGood
	const closedNow = location.temporarilyClosed
	const previewBlocks =
		!preview || preview.obstacles.length > 0 || preview.refusal !== null || preview.recipientRefusal !== null

	const ask = (next: Mode) => {
		setMessage(null)
		setComment('')
		setPreview(null)
		setRecipientId('')
		setMode(next)
	}

	const messageLine = message && (
		<p
			className={message.ok ? 'text-sm text-emerald-700 dark:text-emerald-400' : 'text-sm text-destructive'}
			role={message.ok ? 'status' : 'alert'}
			data-testid='point-closure-message'
		>
			{message.text}
		</p>
	)

	const commentField = (
		<div className='flex flex-col gap-1.5'>
			<Label htmlFor={`closure-comment-${location._id}`}>{t('pointClosure.comment')}</Label>
			<Textarea
				id={`closure-comment-${location._id}`}
				data-testid='point-closure-comment'
				value={comment}
				placeholder={t('pointClosure.commentPlaceholder')}
				onChange={(e) => {
					setComment(e.target.value)
					setMessage(null)
				}}
			/>
		</div>
	)

	const submit = () => (mode === 'close' ? onClose() : onOpen(mode === 'reopen'))

	return (
		<section className='flex flex-col gap-3' data-testid='point-closure'>
			<SectionTitle
				title={t('pointClosure.title')}
				aside={
					closedForGood ? (
						<Button size='sm' variant='outline' disabled={busy} onClick={() => ask('reopen')} data-testid='point-reopen'>
							{t('pointClosure.reopen')}
						</Button>
					) : (
						<>
							{closedNow ? (
								<Button size='sm' disabled={busy} onClick={() => ask('open')} data-testid='point-open'>
									{t('pointClosure.openNow')}
								</Button>
							) : (
								<Button size='sm' variant='outline' disabled={busy} onClick={() => ask('close')} data-testid='point-close-now'>
									{t('pointClosure.closeNow')}
								</Button>
							)}
							<Button
								size='sm'
								variant='destructive'
								disabled={busy}
								onClick={() => ask('forGood')}
								data-testid='point-close-for-good-start'
							>
								{t('pointClosure.closeForGood')}
							</Button>
						</>
					)
				}
			/>
			<p
				className='text-sm'
				data-testid='point-closure-state'
				data-state={closedForGood ? 'for_good' : closedNow ? 'temporarily' : 'open'}
			>
				{closedForGood
					? t('pointClosure.stateClosedForGood', {
							date: formatDayTime(closedForGood.at),
							recipient: closedForGood.recipientCode,
							comment: closedForGood.comment,
						})
					: closedNow
						? t('pointClosure.stateClosed', { date: formatDayTime(closedNow.since), comment: closedNow.comment })
						: t('pointClosure.open')}
			</p>

			{mode === null && messageLine}

			<Dialog
				open={mode === 'close' || mode === 'open' || mode === 'reopen'}
				onOpenChange={(open) => !open && !busy && setMode(null)}
			>
				<DialogContent className='sm:max-w-md' data-testid='point-closure-dialog'>
					<DialogHeader>
						<DialogTitle>
							{mode === 'close'
								? t('pointClosure.closeAsk', { id: location.id })
								: mode === 'reopen'
									? t('pointClosure.reopenAsk', { id: location.id })
									: t('pointClosure.openAsk', { id: location.id })}
						</DialogTitle>
						<DialogDescription>
							{mode === 'close'
								? t('pointClosure.closeNowHint')
								: mode === 'reopen'
									? t('pointClosure.reopenHint')
									: t('pointClosure.openHint')}
						</DialogDescription>
					</DialogHeader>
					{commentField}
					{messageLine}
					<DialogFooter>
						<Button variant='outline' disabled={busy} onClick={() => setMode(null)}>
							{t('pointClosure.cancel')}
						</Button>
						<Button disabled={busy} onClick={submit} data-testid='point-closure-submit'>
							{mode === 'close'
								? t('pointClosure.closeNow')
								: mode === 'reopen'
									? t('pointClosure.reopen')
									: t('pointClosure.openNow')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AlertDialog open={mode === 'forGood'} onOpenChange={(open) => !open && !busy && setMode(null)}>
				<AlertDialogContent className='data-[size=default]:sm:max-w-xl' data-testid='point-close-for-good-dialog'>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('pointClosure.forGoodAsk', { id: location.id })}</AlertDialogTitle>
						<AlertDialogDescription>{t('pointClosure.closeForGoodHint')}</AlertDialogDescription>
					</AlertDialogHeader>
					<div className='flex flex-col gap-3'>
						<div className='flex flex-wrap items-end gap-2'>
							<div className='flex flex-col gap-1.5'>
								<Label htmlFor={`closure-recipient-${location._id}`}>{t('pointClosure.recipient')}</Label>
								<select
									id={`closure-recipient-${location._id}`}
									data-testid='point-closure-recipient'
									className={SELECT_CLASS}
									value={recipientId}
									onChange={(e) => {
										setRecipientId(e.target.value)
										setPreview(null)
									}}
								>
									<option value=''>{t('pointClosure.chooseRecipient')}</option>
									{recipients.map((p) => (
										<option key={p._id} value={p._id}>
											{p.id}
										</option>
									))}
								</select>
							</div>
							<Button variant='outline' disabled={busy || !recipientId} onClick={onPreview} data-testid='point-closure-preview'>
								{t('pointClosure.preview')}
							</Button>
						</div>
						{preview && <PreviewView preview={preview} />}
						{commentField}
						{messageLine}
					</div>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={busy}>{t('pointClosure.cancel')}</AlertDialogCancel>
						<Button
							variant='destructive'
							disabled={busy || !recipientId || previewBlocks}
							onClick={onCloseForGood}
							data-testid='point-close-for-good'
						>
							{t('pointClosure.confirm')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</section>
	)
}

/** The preview of a full closure: what moves, what goes as it is, what is in the way. */
export function PreviewView({ preview }: { preview: ClosurePreview }) {
	const { t } = useTranslate()
	const refusal = preview.refusal ?? preview.recipientRefusal
	return (
		<div className='flex flex-col gap-1.5 rounded-md border p-3 text-sm' data-testid='point-closure-preview-view'>
			<div className='font-medium'>
				{t('pointClosure.previewTitle', { recipient: preview.recipient?.id ?? '—' })}
			</div>
			<ul className='flex flex-col gap-0.5' data-testid='point-closure-counts'>
				{COUNTS.filter((k) => preview.counts[k] > 0).map((k) => (
					<li key={k} data-count={k}>
						{t(`closureCount.${k}`)}: {preview.counts[k]}
					</li>
				))}
			</ul>
			<div className='font-medium' data-testid='point-closure-total'>
				{t('pointClosure.total', { count: String(preview.counts.total) })}
			</div>
			<div>{t('pointClosure.toPartner', { count: String(preview.counts.toPartner) })}</div>
			{preview.bagsWithDrivers > 0 && (
				<div>{t('pointClosure.withDrivers', { count: String(preview.bagsWithDrivers) })}</div>
			)}
			{refusal && (
				<p className='text-destructive' role='alert' data-testid='point-closure-refusal'>
					{t(`closureRefusal.${refusal}` as never)}
				</p>
			)}
			{preview.obstacles.length > 0 ? (
				<div className='flex flex-col gap-0.5 text-destructive' data-testid='point-closure-obstacles'>
					<div className='font-medium'>{t('pointClosure.obstaclesTitle')}</div>
					<ul className='list-inside list-disc'>
						{preview.obstacles.map((o) => (
							<li key={o.kind} data-obstacle={o.kind}>
								{t(`closureObstacle.${o.kind}`, {
									count: String(o.count),
									amount: (o.amount ?? 0).toLocaleString('ru-RU'),
									codes: o.codes.join(', '),
								})}
							</li>
						))}
					</ul>
				</div>
			) : (
				<div className='text-emerald-700 dark:text-emerald-400' data-testid='point-closure-no-obstacles'>
					{t('pointClosure.noObstacles')}
				</div>
			)}
		</div>
	)
}
